// @flow
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import SignProcess from '../model/SignProcess';
import { doGet, doGetBinary } from '../util/api';
import { camelCaseToSentence } from '../util/string';
import {QRCodeCanvas} from 'qrcode.react';
import { AuthContext } from '../context/auth';
import routes from '../routes';
import ApiResponse from '../model/ApiResponse';

const maxSize: number = Number.parseInt(`${process.env.REACT_APP_UPLOAD_LIMIT || '0'}`);
const baseUrl: string = String(process.env.REACT_APP_BASE_URL);

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary['900']
  },
  grey: {
    color: theme.palette.text.secondary
  },
  error: {
    color: theme.palette.error.main
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  marginTop: {
    marginTop: theme.spacing(2)
  },
  marginVertical: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  upload: {
    display: 'none'
  },
  canvas: {
    width: '100%',
    maxWidth: 300,
    border: 'solid black .5px'
  },
  clearButton: {
    float: 'right'
  }
}));

type Props = {
  code: string,
  isRetailClient: boolean,
  isValidation: boolean,
  process: SignProcess,
  expanded: boolean,
  disabled: boolean,
  processing: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: (AgreementProps) => Promise<boolean>,
  contractAgreementUrl: string,
  templateAgreementUrl: string,
  validationUrl: string,
  qrValidationUrl: string,
  canLoad: boolean,
};

const ProcessAgreementData = (props: Props) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const {
    code, isRetailClient, isValidation, process, expanded, disabled: disabledProp,
    onExpanded, onSave, processing, contractAgreementUrl, templateAgreementUrl,
    validationUrl, qrValidationUrl, canLoad,
  } = props;
  const disabled = disabledProp || process.hasContractAgreementApproved;

  const [downloading, setDownloading] = useState<boolean>(false);
  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [tplAccepted, setTplAccepted] = useState<boolean>(false);
  const [file, setFile] = useState<any>();
  const [canvas, setCanvas] = useState<any>();
  const [clientUrl, setClientUrl] = useState<?string>();

  useEffect(() => {
    if (!!process && !disabled && isValidation && tplAccepted && termsChecked && !validationUrl && canLoad) {
      onSave();
    }
  }, [tplAccepted, termsChecked, onSave, process, validationUrl, isValidation, disabled, canLoad]);

  let processStatusText = process.hasAgreementLoaded() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasAgreementLoaded() ? <DoneIcon color='action' /> : <EditIcon color='secondary' />;
  if (process.hasDataValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon color='primary' />;
  }

  const clientPage = () => {
    setDownloading(true);
    doGet(`/customer/sign-process/${process.code}/retail-client-code`)
      .then((response: ApiResponse) => {
        if (response.ok) {
          setClientUrl(`${baseUrl}${routes.contract}/${process.code}/retail/${String(response.data)}`);
        } else if (!response.is401Error()) {
          enqueueSnackbar(t(response.message), {
            variant: 'error'
          });
        }
        setDownloading(false);
      });
  };

  const download = (url: string) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank');
    } else {
      setDownloading(true);
      doGetBinary(url)
        .then((response: ApiResponse) => {
          if (response.ok) {
            window.open(response.blobLink('application/pdf'), '_blank');
          } else if (!response.is401Error()) {
            enqueueSnackbar(t(response.message), {
              variant: 'error'
            });
          }
          setDownloading(false);
        });
    }
  };

  const content = () => {
    const processData = {
      ...process.toDataMap(),
      fullName: process.fullName(),
    };

    const termField = (<Grid item xs={12} sm={12} md={12} lg={12}>
      <FormControlLabel
        control={<Checkbox
          disabled={disabled}
          checked={termsChecked}
          onChange={(e) => {
            setTermsChecked(e.target.checked);
          }}
          name='terms'
          color='primary' />}
        label={<a href='https://ecuafirma.com/condiciones-generales-ac/' target='_blank' rel='noopener noreferrer'>
          {t('Accept terms and conditions')}
        </a>} />
    </Grid>);

    const templateField = (<Grid item xs={12} sm={12} md={12} lg={12}>
      <FormControlLabel
        control={<Checkbox
          disabled={disabled}
          checked={tplAccepted}
          onChange={(e) => {
            setTplAccepted(e.target.checked);
          }}
          name='tplAccepted'
          color='primary' />}
        label={<span>{t('Agreement terms accepted')}</span>} />
      <Button
        disabled={disabled || !tplAccepted || processing || downloading}
        size='small'
        variant='outlined'
        color='secondary'
        startIcon={(processing || downloading) ? <CircularProgress size={10} /> : null}
        onClick={() => {
          download(templateAgreementUrl);
        }}>
        {t('Download template')}
      </Button>
    </Grid>);

    if (process.isNaturalProcessType()) {
      return (<div className={classes.marginVertical}>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {t(process.needTributaryId() ? 'Agreement tributary intro' : 'Agreement intro', processData)}
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {t(process.needTributaryId() ? 'Tributary agreement sign use' : 'Agreement sign use', processData)}
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {t('Agreement intro 2')}
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <p><b>{t(camelCaseToSentence('name'))}</b>:&nbsp;{process.fullName()}</p>
        </Grid>
        {process.needTributaryId() ? <Grid item xs={12} sm={12} md={6} lg={6}>
          <p><b>{t(camelCaseToSentence('tributaryLegalID'))}</b>:&nbsp;{process.tributaryLegalID}</p>
        </Grid> : <Grid item xs={12} sm={12} md={6} lg={6}>
          <p><b>{t(camelCaseToSentence('legalID'))}</b>:&nbsp;{process.legalID}</p>
        </Grid>}
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <p><b>{t(camelCaseToSentence('addressOfResidence'))}</b>:&nbsp;{process.addressOfResidence}</p>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <p><b>{t(camelCaseToSentence('provinceOfResidence'))}</b>:&nbsp;{process.provinceOfResidence}</p>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <p><b>{t(camelCaseToSentence('cityOfResidence'))}</b>:&nbsp;{process.cityOfResidence}</p>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <p><b>{t(camelCaseToSentence('email'))}</b>:&nbsp;{process.email}</p>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <p><b>{t(camelCaseToSentence('phone'))}</b>:&nbsp;{process.phone}</p>
        </Grid>
        {templateField}
        <Grid item xs={12} sm={12} md={12} lg={12}>
          {t('Agreement acknowledge')}
        </Grid>
        {termField}
        {!isValidation && <Grid item xs={12} sm={12} md={12} lg={12}>
          {t('Agreement farewell')}
        </Grid>}
      </div>);
    }
    return (<div className={classes.marginVertical}>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        {t('Legal agreement intro', process)}
      </Grid>
      <Grid container spacing={1} className={classes.marginTop}>
        <Grid item xs={12} sm={12} md={2} lg={1}>
          <b>#</b>
        </Grid>
        <Hidden mdUp>
          <Grid item xs={12} sm={12} md={2} lg={1}>
            1
          </Grid>
        </Hidden>
        <Grid item xs={12} sm={12} md={4} lg={4}>
          <b>{t('Name of people who will use a sign')}</b>
        </Grid>
        <Hidden mdUp>
          <Grid item xs={12} sm={12} md={4} lg={4}>
            {process.name}
          </Grid>
        </Hidden>
        <Grid item xs={12} sm={12} md={3} lg={3}>
          <b>{t('Legal ID')}</b>
        </Grid>
        <Hidden mdUp>
          <Grid item xs={12} sm={12} md={3} lg={3}>
            {process.legalID}
          </Grid>
        </Hidden>
        <Grid item xs={12} sm={12} md={3} lg={3}>
          <b>{t('Role')}</b>
        </Grid>
        <Hidden mdUp>
          <Grid item xs={12} sm={12} md={3} lg={3}>
            {!!process.companyData ? process.companyData.customerCompanyRol : ''}
          </Grid>
        </Hidden>
        <Hidden mdUp>
          <Grid item xs={12} sm={12} md={1} lg={1}>
            &nbsp;
          </Grid>
        </Hidden>
      </Grid>
      <Hidden smDown>
        <Grid container spacing={1} className={classes.marginVertical}>
          <Grid item xs={12} sm={12} md={2} lg={1}>
            1
          </Grid>
          <Grid item xs={12} sm={12} md={4} lg={4}>
            {process.name}
          </Grid>
          <Grid item xs={12} sm={12} md={3} lg={3}>
            {process.legalID}
          </Grid>
          <Grid item xs={12} sm={12} md={3} lg={3}>
            {!!process.companyData ? process.companyData.customerCompanyRol : ''}
          </Grid>
          <Hidden mdUp>
            <Grid item xs={12} sm={12} md={1} lg={1}>
              &nbsp;
            </Grid>
          </Hidden>
        </Grid>
      </Hidden>
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.marginVertical}>
        {t('Legal agreement note')}
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        {t('Agreement intro 2')}
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.marginTop}>
        <b>{t('Legal representative information')}</b>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t('Company name').toUpperCase()}</b>:&nbsp;{!!process.companyData ? process.companyData.name : ''}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{'R.U.C.'.toUpperCase()}</b>:&nbsp;{!!process.companyData ? process.companyData.legalID : ''}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t('Legal representative full name').toUpperCase()}</b>:&nbsp;{process.name}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t('Legal ID').toUpperCase()}</b>:&nbsp;{process.legalID}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t('RUC address').toUpperCase()}</b>:&nbsp;{!!process.companyData ? process.companyData.address : ''}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t('Province').toUpperCase()}</b>:&nbsp;{!!process.companyData ? process.companyData.province : ''}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t('City').toUpperCase()}</b>:&nbsp;{!!process.companyData ? process.companyData.city : ''}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t('Valid company email').toUpperCase()}</b>:&nbsp;{process.email}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t(camelCaseToSentence('phone')).toUpperCase()}</b>:&nbsp;{process.phone}</p>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <p><b>{t('Company phone').toUpperCase()}</b>:&nbsp;{!!process.companyData ? process.companyData.phone : ''}</p>
      </Grid>
      {templateField}
      <Grid item xs={12} sm={12} md={12} lg={12}>
        {t('Agreement acknowledge')}
      </Grid>
      {termField}
      {!isValidation && <Grid item xs={12} sm={12} md={12} lg={12}>
        {t('Agreement farewell')}
      </Grid>}
    </div>);
  };

  return (
    <Accordion
      key={code}
      expanded={expanded}
      onChange={onExpanded(code)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${code}-panel`}
        id={`${code}-panel`}
      >
        {processStatusIcon}&nbsp;
        <Typography className={classes.heading}>
          {t('Contract agreement')}
        </Typography>
        <Typography className={classes.secondaryHeading}>
          {t(processStatusText)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {processing && <Grid item xs={12} sm={12} md={12} lg={12}>
            <CircularProgress />
          </Grid>}
          {(!!user && user.isRetail && !disabled && !isRetailClient) && <Grid item xs={12} sm={12} md={12} lg={12}>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              startIcon={(processing || downloading) ? <CircularProgress size={10} /> : null}
              disabled={processing || downloading}
              onClick={clientPage}>
              {t('Agreement client page')}
            </Button>
          </Grid>}
          {process.hasAgreementLoaded() && <Grid item xs={12} sm={12} md={12} lg={12}>
            <Button
              size='small'
              variant='outlined'
              color='secondary'
              startIcon={(processing || downloading) ? <CircularProgress size={10} /> : null}
              disabled={processing || downloading}
              onClick={() => {
                download(contractAgreementUrl);
              }}>
              {t('See saved agreement')}
            </Button>
          </Grid>}
          {(!disabled && !isValidation) && <Grid item xs={12} sm={12} md={12} lg={12}>
            <>
              <input
                accept='.pdf'
                id='upload-agreement'
                className={classes.upload}
                type='file'
                onChange={(e) => {
                  if (e.target.files
                    && e.target.files.length) {
                    const file = e.target.files[0];
                    if (file.size >= maxSize) {
                      enqueueSnackbar(t('Upload limit is MB_SIZE', { size: (maxSize / (1024 * 1024)).toFixed(0) }), {
                        variant: 'error'
                      });
                    } else {
                      setFile(file);
                    }
                  }
                }}
              />
              <label htmlFor='upload-agreement'>
                {!file && <Button
                  disabled={processing || downloading || disabled || !termsChecked || !tplAccepted}
                  variant='contained'
                  color='secondary'
                  startIcon={processing ? <CircularProgress color='inherit' size='1rem' /> : <AddIcon />}
                  component='span'>
                  {t('Upload agreement')}
                </Button>}
                {!!file && <span>
                  <Typography variant='subtitle2' className={classes.grey}>
                    {t('Upload agreement')}&nbsp;
                    <Button
                      disabled={disabled}
                      variant='contained'
                      color='primary'
                      startIcon={processing ? <CircularProgress color='inherit' size='1rem' /> : <UploadIcon />}
                      aria-label='upload'
                      onClick={() => {
                        onSave({ file, content: null, name: file.name });
                      }}
                    >
                      {t('Upload')}
                    </Button>
                    <IconButton
                      disabled={disabled}
                      aria-label='delete'
                      className={classes.error}
                      onClick={() => {
                        setFile(null);
                      }}>
                      <DeleteIcon />
                    </IconButton>
                  </Typography>
                </span>}
              </label>
            </>
          </Grid>}
          {!disabled && <Grid className={process.hasAgreementLoaded() ? classes.marginTop : ''}>
            {content()}
            {(termsChecked && tplAccepted && isValidation && !disabled && validationUrl) && <Grid item xs={12} sm={12} md={12} lg={12}>
              <Button
                size='small'
                variant='outlined'
                color='primary'
                onClick={() => {
                  window.open(validationUrl, '_blank');
                }}>
                {t('Browse validation link')}
              </Button>
              <br/>
              <br/>
              <b>{t('Browse validation qr')}&nbsp;&nbsp;</b>
              <br/>
              <br/>
              <QRCodeCanvas
                value={qrValidationUrl}
                size='64'
                bgColor="#FFFFFF"
                fgColor="#000000"
                includeMargin={false}
                level="L" />
            </Grid>}
            {(termsChecked && tplAccepted && !isValidation) && <Grid item xs={12} sm={12} md={12} lg={12}>
              <div className={classes.canvas}>
                <SignatureCanvas
                  ref={(ref) => {
                    setCanvas(ref);
                  }}
                  canvasProps={{
                    style: {
                      width: '100%',
                      height: 150
                    }
                  }}
                  clearOnResize={false} />
                <Button
                  size='small'
                  variant='outlined'
                  color='primary'
                  onClick={() => {
                    if (canvas.isEmpty()) {
                      enqueueSnackbar(t('Sign the contract agreement please'), {
                        variant: 'error'
                      });
                    } else {
                      const sign = canvas.getTrimmedCanvas().toDataURL('image/png');
                      onSave({ content: sign, name: null, file: null });
                    }
                  }}>
                  {t('Save')}
                </Button>
                <Button
                  className={classes.clearButton}
                  size='small'
                  variant='outlined'
                  color='secondary'
                  onClick={() => {
                    canvas.clear();
                  }}>
                  {t('Clear')}
                </Button>
              </div>
            </Grid>}
            {(termsChecked && tplAccepted && !isValidation) && <Grid item xs={12} sm={12} md={12} lg={12}>
              {process.name}
            </Grid>}
          </Grid>}
        </Grid>
        <Dialog
          open={!!clientUrl}
          onClose={() => setClientUrl(null)}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogTitle id='alert-dialog-title'>
            {t('Agreement client page')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              {t('Agreement client page description')}
              <br />
              <b>{clientUrl}</b>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClientUrl(null)} color='primary'>
              {t('OK')}
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProcessAgreementData;
